import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Loader2, Users, MapPin, DollarSign, CheckCircle2, XCircle, AlertCircle } from 'lucide-react'
import { ConorbitLoader } from '@/components/ui/ConorbitLoader'

// ─── helpers ─────────────────────────────────────────────────────────────────

function countryFlagEmoji(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return '🌍'
  const codePoints = [...countryCode.toUpperCase()].map(
    c => 0x1F1E6 + c.charCodeAt(0) - 65
  )
  return String.fromCodePoint(...codePoints)
}

const MEMBER_STATUS_BADGE: Record<string, string> = {
  active:    'bg-[#E1F5EE] text-[#0F6E56]',
  pending:   'bg-amber-100 text-amber-800',
  suspended: 'bg-[#FEE8E8] text-[#A32D2D]',
  grace:     'bg-orange-100 text-orange-800',
  expired:   'bg-muted text-muted-foreground',
}

const DUES_BADGE: Record<string, string> = {
  paid:     'bg-[#E1F5EE] text-[#0F6E56]',
  unpaid:   'bg-[#FEE8E8] text-[#A32D2D]',
  exempted: 'bg-muted text-muted-foreground',
}

// ─── props ───────────────────────────────────────────────────────────────────

interface Props {
  associationId: string | null
  onClose: () => void
}

// ─── component ───────────────────────────────────────────────────────────────

export function AssociationDetailSheet({ associationId, onClose }: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [memberSearch, setMemberSearch] = useState('')
  const [removeConfirm, setRemoveConfirm] = useState<string | null>(null)

  // Settings form state
  const [settingsForm, setSettingsForm] = useState<{
    require_membership: boolean
    association_type: string
    dues_amount: string
    dues_currency: string
    dues_period: string
  } | null>(null)

  // ── queries ──────────────────────────────────────────────────────────────

  const { data: assoc, isLoading: assocLoading } = useQuery({
    queryKey: ['assoc-detail', associationId],
    enabled: !!associationId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('associations' as any)
        .select('*')
        .eq('id', associationId!)
        .single() as any)
      if (error) throw error
      // Seed settings form with fetched data
      return data as any
    },
  })

  // Seed settings form when assoc loads (once)
  const seedSettings = (data: any) => {
    if (settingsForm === null && data) {
      setSettingsForm({
        require_membership: !!data.require_membership,
        association_type: data.association_type ?? 'journalist',
        dues_amount: data.dues_amount?.toString() ?? '',
        dues_currency: data.dues_currency ?? 'USD',
        dues_period: data.dues_period ?? 'annual',
      })
    }
  }
  if (assoc && settingsForm === null) seedSettings(assoc)

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['assoc-detail-members', associationId],
    enabled: !!associationId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('association_members' as any)
        .select('*, journalist_profiles(full_name, outlet_name, user_id)')
        .eq('association_id', associationId!)
        .order('created_at', { ascending: false }) as any)
      if (error) throw error
      return (data ?? []) as any[]
    },
  })

  const { data: chapters = [], isLoading: chaptersLoading } = useQuery({
    queryKey: ['assoc-detail-chapters', associationId],
    enabled: !!associationId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('association_chapters' as any)
        .select('*, journalist_profiles(full_name)')
        .eq('association_id', associationId!) as any)
      if (error) throw error
      return (data ?? []) as any[]
    },
  })

  // Build chapter member counts from members list
  const chapterMemberCount = (chapterId: string) =>
    members.filter((m: any) => m.chapter_id === chapterId).length

  // ── mutations ─────────────────────────────────────────────────────────────

  const invalidateMembers = () => qc.invalidateQueries({ queryKey: ['assoc-detail-members', associationId] })

  const markDuesPaidMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const duesPaidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10)
      const { error } = await (supabase
        .from('association_members' as any)
        .update({ dues_status: 'paid', dues_paid_until: duesPaidUntil })
        .eq('id', memberId) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Dues marked as paid.' }); invalidateMembers() },
    onError: () => toast({ title: 'Failed to mark dues paid.', variant: 'destructive' }),
  })

  const suspendMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await (supabase
        .from('association_members' as any)
        .update({ status: 'suspended', suspended_at: new Date().toISOString() })
        .eq('id', memberId) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Member suspended.' }); invalidateMembers() },
    onError: () => toast({ title: 'Failed to suspend member.', variant: 'destructive' }),
  })

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await (supabase
        .from('association_members' as any)
        .delete()
        .eq('id', memberId) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Member removed.' })
      setRemoveConfirm(null)
      invalidateMembers()
    },
    onError: () => toast({ title: 'Failed to remove member.', variant: 'destructive' }),
  })

  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      if (!settingsForm) return
      const { error } = await (supabase
        .from('associations' as any)
        .update({
          require_membership: settingsForm.require_membership,
          association_type: settingsForm.association_type || null,
          dues_amount: parseFloat(settingsForm.dues_amount) || 0,
          dues_currency: settingsForm.dues_currency,
          dues_period: settingsForm.dues_period,
        })
        .eq('id', associationId!) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Settings saved.' })
      qc.invalidateQueries({ queryKey: ['assoc-detail', associationId] })
      qc.invalidateQueries({ queryKey: ['associations'] })
    },
    onError: () => toast({ title: 'Failed to save settings.', variant: 'destructive' }),
  })

  // ── derived ───────────────────────────────────────────────────────────────

  const filteredMembers = memberSearch.trim()
    ? members.filter((m: any) => {
        const q = memberSearch.toLowerCase()
        const name = (m.journalist_profiles?.full_name ?? '').toLowerCase()
        const outlet = (m.journalist_profiles?.outlet_name ?? '').toLowerCase()
        return name.includes(q) || outlet.includes(q)
      })
    : members

  const assocIsActive = assoc?.status === 'active'

  // ── type badge ───────────────────────────────────────────────────────────

  function typeBadgeClass(type: string | undefined) {
    if (type === 'institutional') return 'bg-purple-100 text-purple-800'
    return 'bg-blue-100 text-blue-800'
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Sheet open={!!associationId} onOpenChange={o => { if (!o) onClose() }}>
        <SheetContent side="right" className="w-[600px] sm:w-[700px] overflow-y-auto">
          {assocLoading ? (
            <ConorbitLoader variant="inline" />
          ) : (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle className="text-[16px]">{assoc?.name}</SheetTitle>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${typeBadgeClass(assoc?.association_type)}`}>
                    {assoc?.association_type === 'institutional' ? 'Institutional' : 'Journalist'}
                  </span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${assoc?.status === 'active' ? 'bg-[#E1F5EE] text-[#0F6E56]' : 'bg-[#FEE8E8] text-[#A32D2D]'}`}>
                    {assoc?.status}
                  </span>
                </div>
              </SheetHeader>

              <Tabs defaultValue="members">
                <TabsList className="mb-4">
                  <TabsTrigger value="members">Members</TabsTrigger>
                  <TabsTrigger value="chapters">Chapters</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                {/* ── MEMBERS ── */}
                <TabsContent value="members" className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[12px] text-muted-foreground">
                      {members.length} member{members.length !== 1 ? 's' : ''}
                    </p>
                    <Input
                      placeholder="Search by name or outlet…"
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      className="max-w-[220px] h-8 text-[12px]"
                    />
                  </div>

                  {membersLoading ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredMembers.length === 0 ? (
                    <div className="text-center py-10 text-[13px] text-muted-foreground">No members yet.</div>
                  ) : (
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ backgroundColor: '#0D2240' }}>
                            {['Name', 'Outlet', 'Status', 'Dues', 'Since', 'Actions'].map(h => (
                              <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold text-[11px]">
                                {h}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredMembers.map((m: any, idx: number) => (
                            <TableRow key={m.id} className={idx % 2 === 1 ? 'bg-[#F7F5F0]' : ''}>
                              <TableCell className="text-[12px] font-medium">
                                {m.journalist_profiles?.full_name ?? 'Unknown'}
                              </TableCell>
                              <TableCell className="text-[11px] text-muted-foreground">
                                {m.journalist_profiles?.outlet_name ?? '—'}
                              </TableCell>
                              <TableCell>
                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${MEMBER_STATUS_BADGE[m.status] ?? 'bg-muted text-muted-foreground'}`}>
                                  {m.status}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${DUES_BADGE[m.dues_status] ?? 'bg-muted text-muted-foreground'}`}>
                                  {m.dues_status}
                                </span>
                              </TableCell>
                              <TableCell className="text-[11px] text-muted-foreground">
                                {m.applied_at
                                  ? new Date(m.applied_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {assocIsActive && (
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[10px] px-2 border-[#E1F5EE] text-[#0F6E56] hover:bg-[#E1F5EE]"
                                      disabled={markDuesPaidMutation.isPending}
                                      onClick={() => markDuesPaidMutation.mutate(m.id)}
                                    >
                                      <CheckCircle2 className="w-3 h-3 mr-1" />Mark Dues Paid
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[10px] px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                      disabled={suspendMemberMutation.isPending}
                                      onClick={() => suspendMemberMutation.mutate(m.id)}
                                    >
                                      <AlertCircle className="w-3 h-3 mr-1" />Suspend
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[10px] px-2 border-[#FEE8E8] text-[#A32D2D] hover:bg-[#FEE8E8]"
                                      onClick={() => setRemoveConfirm(m.id)}
                                    >
                                      <XCircle className="w-3 h-3 mr-1" />Remove
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>

                {/* ── CHAPTERS ── */}
                <TabsContent value="chapters" className="space-y-3">
                  {chaptersLoading ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : chapters.length === 0 ? (
                    <div className="text-center py-10 text-[13px] text-muted-foreground">No chapters defined.</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {chapters.map((ch: any) => (
                        <Card key={ch.id} className="border border-border">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{countryFlagEmoji(ch.country_code)}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium truncate">{ch.country_name}</div>
                                {ch.journalist_profiles?.full_name && (
                                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {ch.journalist_profiles.full_name}
                                  </div>
                                )}
                                <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                  <MapPin className="w-3 h-3" />
                                  {chapterMemberCount(ch.id)} member{chapterMemberCount(ch.id) !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ── SETTINGS ── */}
                <TabsContent value="settings" className="space-y-5">
                  {settingsForm && (
                    <>
                      {/* Require membership */}
                      <div className="flex items-center justify-between rounded-lg border border-border p-4">
                        <div>
                          <Label className="text-[13px] font-medium">Require membership for accreditation</Label>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            When enabled, journalists must be members of this association to be accredited.
                          </p>
                        </div>
                        <Switch
                          checked={settingsForm.require_membership}
                          onCheckedChange={v => setSettingsForm(f => f ? { ...f, require_membership: v } : f)}
                        />
                      </div>

                      {/* Association type */}
                      <div className="space-y-1.5">
                        <Label className="text-[12px] font-medium text-muted-foreground">Association Type</Label>
                        <select
                          value={settingsForm.association_type}
                          onChange={e => setSettingsForm(f => f ? { ...f, association_type: e.target.value } : f)}
                          className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background focus:outline-none focus:border-[#006B3C]"
                        >
                          <option value="journalist">Journalist</option>
                          <option value="institutional">Institutional</option>
                        </select>
                      </div>

                      {/* Dues amount + currency */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-[12px] font-medium text-muted-foreground">
                            <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />Dues Amount</span>
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={settingsForm.dues_amount}
                            onChange={e => setSettingsForm(f => f ? { ...f, dues_amount: e.target.value } : f)}
                            className="text-[13px]"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[12px] font-medium text-muted-foreground">Currency</Label>
                          <select
                            value={settingsForm.dues_currency}
                            onChange={e => setSettingsForm(f => f ? { ...f, dues_currency: e.target.value } : f)}
                            className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background focus:outline-none focus:border-[#006B3C]"
                          >
                            {['USD', 'EUR', 'GBP', 'XOF', 'NGN'].map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Dues period */}
                      <div className="space-y-1.5">
                        <Label className="text-[12px] font-medium text-muted-foreground">Dues Period</Label>
                        <select
                          value={settingsForm.dues_period}
                          onChange={e => setSettingsForm(f => f ? { ...f, dues_period: e.target.value } : f)}
                          className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background focus:outline-none focus:border-[#006B3C]"
                        >
                          <option value="annual">Annual</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>

                      <Button
                        className="w-full text-white hover:opacity-90"
                        style={{ backgroundColor: '#006B3C' }}
                        disabled={saveSettingsMutation.isPending}
                        onClick={() => saveSettingsMutation.mutate()}
                      >
                        {saveSettingsMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Save Settings
                      </Button>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Remove confirmation modal */}
      {removeConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-xs p-6">
            <h3 className="text-[14px] font-semibold mb-2">Remove member?</h3>
            <p className="text-[12px] text-muted-foreground mb-5">
              This will permanently remove this member from the association. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setRemoveConfirm(null)}
                className="flex-1 py-2 border border-border rounded-lg text-[13px] hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => removeMemberMutation.mutate(removeConfirm)}
                disabled={removeMemberMutation.isPending}
                className="flex-1 py-2 bg-[#A32D2D] text-white rounded-lg text-[13px] font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {removeMemberMutation.isPending ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
